import axios from 'axios';
import { sessionManager } from './utils/auth';

// Use current domain for backend URL to support custom domains
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

console.log('🌐 Backend URL:', BACKEND_URL);

const API = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add request interceptor to include session token from localStorage
API.interceptors.request.use(
  (config) => {
    const sessionToken = sessionManager.getToken();
    if (sessionToken) {
      config.headers.Authorization = `Bearer ${sessionToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.log('❌ 401 Unauthorized - Clearing session');
      sessionManager.clearToken();
      if (window.location.pathname !== '/login' && window.location.pathname !== '/auth/callback') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const auth = {
  session: (sessionId) => API.post('/auth/session', { session_id: sessionId }),
  me: () => API.get('/auth/me'),
  logout: () => API.post('/auth/logout'),
  signup: (data) => API.post('/auth/signup', data),
  login: (data) => API.post('/auth/login', data)
};

// Business API
export const businesses = {
  getAll: () => API.get('/businesses'),
  create: (data) => API.post('/businesses', data),
  update: (id, data) => API.put(`/businesses/${id}`, data),
  delete: (id) => API.delete(`/businesses/${id}`),
  holdingDiagnostic: (id) => API.get(`/businesses/${id}/holding-diagnostic`),
};

// Invoice API
export const invoices = {
  getAll: (businessId) => API.get(`/invoices?business_id=${businessId}`),
  create: (data) => API.post('/invoices', data),
  update: (id, data) => API.put(`/invoices/${id}`, data),
  delete: (id) => API.delete(`/invoices/${id}`),
  export: (id, format) => API.get(`/invoices/${id}/export?format=${format}`, { 
    responseType: 'blob' 
  }),
  sendReminder: (id) => API.post(`/invoices/${id}/send-reminder`)
};

// Saved Line Items API
export const savedItems = {
  getAll: (businessId) => API.get(`/saved-items?business_id=${businessId}`),
  create: (data) => API.post('/saved-items', data),
  delete: (id) => API.delete(`/saved-items/${id}`)
};

// Products & Services API
export const productsServices = {
  getAll: (businessId) => API.get(`/products-services?business_id=${businessId}`),
  create: (data) => API.post('/products-services', data),
  update: (id, data) => API.put(`/products-services/${id}`, data),
  delete: (id) => API.delete(`/products-services/${id}`)
};

// Invoice Settings API
export const invoiceSettings = {
  get: (businessId) => API.get(`/invoice-settings/${businessId}`),
  update: (businessId, data) => API.put(`/invoice-settings/${businessId}`, data)
};

// Cash Injections API (update existing)
export const cashInjections = {
  getAll: (businessId) => API.get(`/cash-injections?business_id=${businessId}`),
  create: (data) => API.post('/cash-injections', data),
  update: (id, data) => API.put(`/cash-injections/${id}`, data),
  delete: (id) => API.delete(`/cash-injections/${id}`)
};

export const divisionTransfers = {
  list: (businessId) => API.get(`/division-transfers?business_id=${businessId}`),
  create: (data) => API.post('/division-transfers', data),
  delete: (id) => API.delete(`/division-transfers/${id}`)
};

// Email Notifications API
export const notifications = {
  sendOverdueReminders: (businessId) => API.post(`/notifications/send-overdue-reminders?business_id=${businessId}`),
  getEmailLogs: (businessId) => API.get(`/notifications/email-logs?business_id=${businessId}`)
};

// Expense API
export const expenses = {
  getAll: (businessId) => API.get(`/expenses?business_id=${businessId}`),
  create: (data) => API.post('/expenses', data),
  update: (id, data) => API.put(`/expenses/${id}`, data),
  delete: (id) => API.delete(`/expenses/${id}`)
};

// Expense Categories API
export const expenseCategories = {
  get: (businessId) => API.get(`/expense-categories/${businessId}`),
  update: (businessId, categories) => API.put(`/expense-categories/${businessId}`, { categories })
};

// Payment Methods API
export const paymentMethods = {
  getAll: (businessId) => API.get(`/payment-methods?business_id=${businessId}`),
  create: (data) => API.post('/payment-methods', data),
  update: (id, data) => API.put(`/payment-methods/${id}`, data),
  delete: (id) => API.delete(`/payment-methods/${id}`)
};

// Reports API
export const reports = {
  getDashboard: (businessId) => API.get(`/reports/dashboard?business_id=${businessId}`),
  getTrialBalance: (businessId, params = {}) => API.get('/reports/trial-balance', { params: { business_id: businessId, ...params } }),
  getGeneralLedger: (businessId, params = {}) => API.get('/reports/general-ledger', { params: { business_id: businessId, ...params } }),
  getArAging: (businessId, params = {}) => API.get('/reports/ar-aging', { params: { business_id: businessId, ...params } }),
  getApAging: (businessId, params = {}) => API.get('/reports/ap-aging', { params: { business_id: businessId, ...params } }),
  exportAccounting: (businessId, type, format, params = {}) => API.get('/reports/accounting/export', { params: { business_id: businessId, type, format, ...params }, responseType: 'blob' }),
  export: (businessId, type, format) => API.get(
    `/reports/export?business_id=${businessId}&type=${type}&format=${format}`, 
    { responseType: 'blob' }
  ),
  // Sales reports
  getSales: (businessId, startDate = null, endDate = null) => {
    let url = `/reports/sales?business_id=${businessId}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return API.get(url);
  },
  exportSales: (businessId, format, startDate = null, endDate = null) => {
    let url = `/reports/sales/export?business_id=${businessId}&format=${format}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return API.get(url, { responseType: 'blob' });
  },
  // Expense reports
  getExpenses: (businessId, startDate = null, endDate = null, category = null) => {
    let url = `/reports/expenses?business_id=${businessId}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    if (category) url += `&category=${encodeURIComponent(category)}`;
    return API.get(url);
  },
  exportExpenses: (businessId, format, startDate = null, endDate = null, category = null) => {
    let url = `/reports/expenses/export?business_id=${businessId}&format=${format}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    if (category) url += `&category=${encodeURIComponent(category)}`;
    return API.get(url, { responseType: 'blob' });
  },
  // Balance Sheet
  getBalanceSheet: (businessId, startDate = null, endDate = null) => {
    let url = `/reports/balance-sheet?business_id=${businessId}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return API.get(url);
  },
  exportBalanceSheet: (businessId, format, startDate = null, endDate = null) => {
    let url = `/reports/balance-sheet/export?business_id=${businessId}&format=${format}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return API.get(url, { responseType: 'blob' });
  },
  // Cash Flow
  getCashFlow: (businessId, startDate = null, endDate = null) => {
    let url = `/reports/cash-flow?business_id=${businessId}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return API.get(url);
  },
  exportCashFlow: (businessId, format, startDate = null, endDate = null) => {
    let url = `/reports/cash-flow/export?business_id=${businessId}&format=${format}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return API.get(url, { responseType: 'blob' });
  },
  // Division P&L (Legal Entity only)
  getDivisionPnl: (businessId, startDate = null, endDate = null, includeAllocation = true) => {
    let url = `/reports/division-pnl?business_id=${businessId}&include_allocation=${includeAllocation ? 'true' : 'false'}`;
    if (startDate) url += `&start_date=${startDate}`;
    if (endDate) url += `&end_date=${endDate}`;
    return API.get(url);
  }
};

// Overhead Allocation API (management accounting — never posted to GL)
export const overheadAllocation = {
  preview: (payload) => API.post('/overhead-allocations/preview', payload),
  list: (businessId, status = null, period = null) => {
    let url = `/overhead-allocations?business_id=${businessId}`;
    if (status) url += `&status=${status}`;
    if (period) url += `&period=${period}`;
    return API.get(url);
  },
  get: (id) => API.get(`/overhead-allocations/${id}`),
  create: (payload) => API.post('/overhead-allocations', payload),
  update: (id, payload) => API.put(`/overhead-allocations/${id}`, payload),
  post: (id) => API.post(`/overhead-allocations/${id}/post`),
  reverse: (id) => API.post(`/overhead-allocations/${id}/reverse`),
  remove: (id) => API.delete(`/overhead-allocations/${id}`)
};

// Clients API
export const clients = {
  getAll: (businessId) => API.get(`/clients?business_id=${businessId}`),
  create: (data) => API.post('/clients', data),
  update: (id, data) => API.put(`/clients/${id}`, data),
  delete: (id) => API.delete(`/clients/${id}`)
};

// Estimates API
export const estimates = {
  getAll: (businessId) => API.get(`/estimates?business_id=${businessId}`),
  create: (data) => API.post('/estimates', data),
  update: (id, data) => API.put(`/estimates/${id}`, data),
  delete: (id) => API.delete(`/estimates/${id}`),
  export: (id, format) => API.get(`/estimates/${id}/export?format=${format}`, { responseType: 'blob' }),
  convertToInvoice: (id) => API.post(`/estimates/${id}/convert-to-invoice`)
};

// Subscription API
export const subscription = {
  getStatus: () => API.get('/subscription/status'),
  createCheckout: () => API.post('/subscription/checkout'),
  getCheckoutStatus: (sessionId) => API.get(`/subscription/checkout/status/${sessionId}`),
  // PayPal
  createPayPalOrder: () => API.post('/subscription/paypal/create-order'),
  capturePayPalOrder: (orderId) => API.post(`/subscription/paypal/capture-order/${orderId}`),
  getPayPalOrderStatus: (orderId) => API.get(`/subscription/paypal/order-status/${orderId}`)
};

// Admin API
export const admin = {
  getUserStats: () => API.get('/admin/user-stats'),
  getVisitorStats: () => API.get('/admin/visitor-stats'),
  getUserActivity: (params = {}) => API.get('/admin/user-activity', { params }),
  getUserActivityDetail: (userId) => API.get(`/admin/user-activity/${userId}`),
  getActiveSubscribers: () => API.get('/admin/active-subscribers'),
  updateUserSubscription: (userId, data) => API.post(`/admin/users/${userId}/subscription`, data),
};

// Analytics tracking (fire-and-forget)
export const analytics = {
  trackVisit: (data) => API.post('/analytics/track', data).catch(() => {})
};

// Notification Preferences API
export const notificationPrefs = {
  get: (businessId) => API.get(`/notification-prefs/${businessId}`),
  update: (businessId, data) => API.put(`/notification-prefs/${businessId}`, data)
};

export default API;
